import { CommandPalette } from "@components/CommandPalette";
import { GlobalCommandPaletteHotkey } from "@components/CommandPalette/GlobalHotkey";
import {
  GlobalShortcutsHotkey,
  KeyboardShortcutsDialog,
} from "@components/KeyboardShortcutsDialog";
import { NotificationDrawer } from "@components/NotificationDrawer";
import { useCurrentUser } from "@features/auth";
import { TrialBanner } from "@features/billing";
import { CopilotRail, CopilotRailEdge } from "@features/chat";
import { DraftSheet } from "@features/drafts";
import { Box } from "@mui/material";
import { useLayoutStore } from "@stores/layoutStore";
import { useProjectStore } from "@stores/projectStore";
import { useMatchRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Header } from "./Header";
import { InstrumentRack } from "./InstrumentRack";
import { Sidebar } from "./Sidebar";
import {
  useAppNavMode,
  useCopilotRailMode,
  useCopilotRailPlacement,
  useIsChatSurface,
} from "./useLayoutMode";

interface AppLayoutProps {
  children: ReactNode;
}

function usePageTitle(): string {
  const matchRoute = useMatchRoute();
  if (matchRoute({ to: "/projects/$projectId/overview" })) return "Overview";
  if (matchRoute({ to: "/projects/$projectId/brief" })) return "Brief";
  if (matchRoute({ to: "/projects/$projectId/specs" })) return "Specs";
  if (matchRoute({ to: "/projects/$projectId/cables" })) return "Cables";
  if (matchRoute({ to: "/projects/$projectId/bom" })) return "BOM";
  if (matchRoute({ to: "/projects/$projectId/pcb" })) return "PCB";
  if (matchRoute({ to: "/projects/$projectId/connectors" }))
    return "Connectors";
  if (matchRoute({ to: "/projects/$projectId/versions" })) return "Versions";
  if (matchRoute({ to: "/projects/$projectId/circuits" })) return "Circuits";
  if (matchRoute({ to: "/projects/$projectId/sync" })) return "Plugins";
  if (matchRoute({ to: "/projects/$projectId/chat" })) return "Chat";
  if (matchRoute({ to: "/chat" })) return "Chat";
  if (matchRoute({ to: "/projects" })) return "Projects";
  if (matchRoute({ to: "/history" })) return "History";
  return "Circuit Forge";
}

/**
 * Application chrome:
 *
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │  ┌────────┐  ┌──────────────────┐  ┌──────────┐  ┌────────────┐ │
 *   │  │        │  │  Header          │  │          │  │            │ │
 *   │  │        │  │  TrialBanner     │  │ Draft    │  │  Copilot   │ │
 *   │  │ Sidebar│  │                  │  │  Sheet   │  │   Rail     │ │
 *   │  │        │  │  <main> children │  │          │  │            │ │
 *   │  └────────┘  └──────────────────┘  └──────────┘  └────────────┘ │
 *   ├──────────────────────────────────────────────────────────────────┤
 *   │  Instrument Rack                                    (full width) │
 *   └──────────────────────────────────────────────────────────────────┘
 *
 * The DraftSheet is owned by AppLayout (not by individual pages) so the
 * draft surface persists across navigation — open the panel on /chat,
 * navigate to /projects, the panel stays. Lifts projectId / versionId
 * from the project store so the commit gesture targets the right
 * project regardless of which page is rendered into <main>.
 *
 * The Copilot rail is the persistent chat companion — same conversation
 * as `/chat`, mounted at the app level so it survives navigation. Hidden
 * on chat surfaces (page IS the conversation there) and when no project
 * is active. See `useCopilotRailMode` for the full visibility rule.
 */
export function AppLayout({ children }: AppLayoutProps) {
  // Keep `/auth/me` actively subscribed so the query revalidates on
  // window focus / interval and a server-side session expiry surfaces
  // through the axios 401 interceptor. The route's `beforeLoad` already
  // gated entry; this call is purely the active-subscription anchor.
  useCurrentUser();
  const navMode = useAppNavMode();
  const isChat = useIsChatSurface();
  const copilotRailMode = useCopilotRailMode();
  const copilotPlacement = useCopilotRailPlacement();
  const pageTitle = usePageTitle();

  const drawerOpen = useLayoutStore((s) => s.appNavDrawerOpen);
  const setDrawerOpen = useLayoutStore((s) => s.setAppNavDrawerOpen);
  const toggleDrawer = useLayoutStore((s) => s.toggleAppNavDrawer);
  const toggleChatHistoryDrawer = useLayoutStore(
    (s) => s.toggleChatHistoryDrawer,
  );

  const projectId = useProjectStore((s) => s.activeProject);
  const versionId = useProjectStore((s) => s.activeVersionId);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      {/* React 19 hoists this <title> into <head> — gives every
          protected route a meaningful tab title without per-page wiring. */}
      <title>{`${pageTitle} · Circuit Forge`}</title>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          minHeight: 0,
          // Anchor for the overlay-mode Copilot rail on tablet, where it
          // floats absolutely so the workbench width never changes when
          // the rail toggles open/closed.
          position: "relative",
        }}
      >
        <Sidebar
          mode={navMode}
          drawerOpen={drawerOpen}
          onDrawerClose={() => setDrawerOpen(false)}
        />

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {navMode === "drawer" && (
            <Header
              pageTitle={pageTitle}
              onMenuClick={toggleDrawer}
              showChatHistoryButton={isChat}
              onChatHistoryClick={toggleChatHistoryDrawer}
            />
          )}
          <TrialBanner />
          <Box
            component="main"
            sx={{
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              // Reserve scrollbar space so child surfaces that toggle
              // between scrolling/non-scrolling content don't shift the
              // viewport horizontally as the gutter appears/disappears.
              scrollbarGutter: "stable",
            }}
          >
            {children}
          </Box>
        </Box>

        <DraftSheet projectId={projectId} parentVersionId={versionId} />

        {/* On tablet (overlay placement) keep the edge tab mounted even
            when the rail is open, so toggling never reflows main; the
            open rail floats absolutely above content. On desktop (docked
            placement) only one of edge/rail is mounted at a time. */}
        {copilotPlacement === "overlay" && copilotRailMode !== "hidden" && (
          <CopilotRailEdge />
        )}
        {copilotRailMode === "open" && projectId && (
          <CopilotRail projectId={projectId} placement={copilotPlacement} />
        )}
        {copilotRailMode === "closed" && copilotPlacement === "docked" && (
          <CopilotRailEdge />
        )}
      </Box>

      <InstrumentRack />

      {/* Cmd+K / Ctrl+K command palette — global hotkey + modal mount.
          Mounted once at the application root so any feature can
          push commands into it via useCommandPalette + useCommands. */}
      <GlobalCommandPaletteHotkey />
      <CommandPalette />

      {/* `?` keyboard-shortcuts cheat sheet — read-only counterpart
          to the palette. Same chassis, taught semantics. */}
      <GlobalShortcutsHotkey />
      <KeyboardShortcutsDialog />

      {/* Notification feed — right-anchored drawer mounted once at the
          app root. Features push via useNotifications.push(). */}
      <NotificationDrawer />
    </Box>
  );
}
